import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { RoomService } from "@/services/RoomService";

interface UseLocationRoomsQueryOptions {
  locationSlug: string;
  enabled?: boolean;
}

/**
 * React Query hook to fetch all rooms for a specific location
 *
 * Query Key: ["rooms", locationSlug]
 * Cache: 15 minutes (room configurations rarely change)
 *
 * @param locationSlug - The location slug to fetch rooms for
 * @param enabled - Whether the query should run (default: true)
 */
export function useLocationRoomsQuery({
  locationSlug,
  enabled = true,
}: UseLocationRoomsQueryOptions) {
  const roomService = useMemo(() => new RoomService(), []);

  return useQuery({
    queryKey: ["rooms", locationSlug],
    queryFn: () =>
      roomService.getAllRoomsForLocation(locationSlug, undefined, {
        field: "displayOrder",
        order: "asc",
      }),
    enabled: enabled && !!locationSlug,
    staleTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
}
